/** Pure O008 one-page boundary shared by the route and future browser collector.
 * It validates shape and available source/revision/coordinate bindings only.
 * Snapshot membership and service authorization remain database facts. */
import { validateSourceTurnV1, type SourceTurnV1 } from './contracts';
import { validateTranslationRevisionRecord, REVISION_UNAVAILABLE_REASON_CODES,
  type AttachedTranslationRevisionV1, type RevisionUnavailableReasonCode } from './revisionEnvelope';
import { validateEngineCoordinateV1, type EngineCoordinateV1 } from './engineCoordinate';
import { CAPTION_HYDRATION_LIMITS } from './captionHydration';

const CONTRACT_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const DATABASE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const V3_PAGE_CONTRACT = 'live-translation.caption-hydration-page.v3';
const V3_CURSOR_CONTRACT = 'live-translation.caption-hydration-cursor.v3';
const VISIBILITY_SNAPSHOT = /^[0-9]+:[0-9]+:(?:[0-9]+(?:,[0-9]+)*)?$/;
// Exact persisted legacy_sync_id CHECK in 20261092 and current 20261149 append RPC.
const LEGACY_SYNC_ID_CHARS = 256;
const MIN_SELECTION_TIME_MS = 1704067200000;
const UNAVAILABLE_REASONS: ReadonlySet<string> = new Set(REVISION_UNAVAILABLE_REASON_CODES);
const PHASES = ['source_turns', 'revisions', 'selections', 'unavailable'] as const;
export type HydrationPhase = (typeof PHASES)[number];

const PAGE_KEYS = [
  'contract_version',
  'call_id',
  'target_language',
  'visibility_snapshot',
  'phase',
  'page_size',
  'items',
  'cursor',
  'next_cursor',
  'has_more',
  'complete',
  'authorized_worker_identities_valid_for_ms',
  'authorized_worker_identities',
] as const;
const CURSOR_BASE_KEYS = [
  'contract_version',
  'call_id',
  'target_language',
  'visibility_snapshot',
  'phase',
] as const;
const CURSOR_AFTER_KEYS: Record<HydrationPhase, readonly string[]> = {
  source_turns: [
    'after_stable_participant_id',
    'after_source_epoch',
    'after_ordinal',
    'after_source_turn_id',
  ],
  revisions: ['after_source_turn_id', 'after_revision_index', 'after_translation_revision_id'],
  selections: ['after_source_turn_id'],
  unavailable: ['after_source_turn_id'],
};
const SOURCE_ITEM_KEYS = ['source_turn', 'transcript_sync_id'] as const;
// O008 obtains this closed 18-key object from the persisted v2 source reader.
// The shared v1 schema keeps detected_language optional for older live senders;
// an authenticated hydration row always supplies the key, including null.
const DB_SOURCE_TURN_KEYS = [
  'contract_version', 'source_turn_id', 'call_id', 'stable_participant_id',
  'source_track_sid', 'detected_language', 'source_epoch', 'ordinal',
  'speaker_role', 'source_language', 'assignment_generation',
  'started_at_ms', 'ended_at_ms', 'text', 'deepgram_segment_id',
  'deepgram_confidence', 'finality', 'created_at',
] as const;
const UNAVAILABLE_ITEM_KEYS = ['source_turn_id', 'target_language', 'reason_code'] as const;
const V3_PAGE_KEYS = [...PAGE_KEYS, 'outcome'] as const;
const V3_REVISION_ITEM_KEYS = ['revision', 'coordinate'] as const;
const V3_SELECTED_ITEM_KEYS = [
  'source_turn', 'transcript_sync_id', 'revision', 'selection', 'coordinate',
] as const;
const V3_SELECTION_KEYS = [
  'source_turn_id', 'target_language', 'selected_revision_id',
  'selection_generation', 'selection_reason', 'selected_at_ms',
] as const;
const V3_SELECTION_REASONS: ReadonlySet<string> = new Set([
  'initial', 'quality_revision', 'human_review', 'recovery',
]);
const COORDINATE_KEYS = [
  'contract_version', 'call_id', 'translation_revision_id', 'source_turn_id',
  'translation_session_record_id', 'source_stable_participant_id',
  'source_epoch', 'source_track_sid', 'target_language',
  'assignment_generation', 'route_generation', 'allocation_group_id',
  'engine_range_start', 'engine_range_end',
] as const;
const NIL_UUID = '00000000-0000-0000-0000-000000000000';

export interface ParsedCursor {
  raw: Record<string, unknown>;
  phase: HydrationPhase;
  visibilitySnapshot: string;
  hasAfter: boolean;
  canonical: string;
  afterCanonical: string | null;
}

export interface CaptionHydrationV3Selection {
  source_turn_id: string;
  target_language: string;
  selected_revision_id: string;
  selection_generation: number;
  selection_reason: 'initial' | 'quality_revision' | 'human_review' | 'recovery';
  selected_at_ms: number;
}

export type CaptionHydrationV3Item =
  | { source_turn: SourceTurnV1; transcript_sync_id: string }
  | { revision: AttachedTranslationRevisionV1; coordinate: EngineCoordinateV1 | null }
  | { source_turn: SourceTurnV1; transcript_sync_id: string;
      revision: AttachedTranslationRevisionV1; selection: CaptionHydrationV3Selection;
      coordinate: EngineCoordinateV1 | null }
  | { source_turn_id: string; target_language: string;
      reason_code: RevisionUnavailableReasonCode };

export interface CaptionHydrationV3Page {
  contract_version: typeof V3_PAGE_CONTRACT;
  call_id: string;
  target_language: string;
  visibility_snapshot: string;
  phase: HydrationPhase;
  page_size: number;
  items: CaptionHydrationV3Item[];
  cursor: Record<string, unknown> | null;
  next_cursor: Record<string, unknown> | null;
  has_more: boolean;
  complete: boolean;
  authorized_worker_identities_valid_for_ms: number;
  authorized_worker_identities: string[];
  outcome: 'page' | 'snapshot_changed';
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, expected: readonly string[]): boolean {
  const keys = Object.keys(value);
  return keys.length === expected.length && expected.every((key) => keys.includes(key));
}

function isSafeNonnegativeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

function codePointLength(value: string): number {
  // The string iterator counts Unicode code points, including astral pairs,
  // without materializing a character array for a complete long revision.
  let count = 0;
  for (const _codePoint of value) count += 1;
  return count;
}

/** PostgreSQL btrim(text) without an explicit character set removes ASCII
 * spaces only. The persisted source append RPC rejects empty, padded or
 * ASCII-space-only legacy_sync_id; it permits tab/NBSP boundary characters. */
function validPersistedTranscriptSyncId(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const length = codePointLength(value);
  return length > 0 && length <= LEGACY_SYNC_ID_CHARS &&
    !value.startsWith(' ') && !value.endsWith(' ');
}

function validDbSourceTurn(value: unknown): value is SourceTurnV1 {
  return isPlainRecord(value) && hasExactKeys(value, DB_SOURCE_TURN_KEYS) &&
    validateSourceTurnV1(value).valid;
}

function phaseIndex(phase: HydrationPhase): number {
  return PHASES.indexOf(phase);
}

function readCursor(
  value: unknown,
  expected: { callUuid: string; targetLanguage: string },
): ParsedCursor | null {
  if (!isPlainRecord(value)) return null;
  // O008 delegates to the v2 reader's 65,536-byte cursor ceiling.
  try {
    if (new TextEncoder().encode(JSON.stringify(value)).byteLength >
        CAPTION_HYDRATION_LIMITS.visibilitySnapshotChars) return null;
  } catch {
    return null;
  }
  if (value.contract_version !== V3_CURSOR_CONTRACT) return null;
  if (value.call_id !== expected.callUuid || value.target_language !== expected.targetLanguage) {
    return null;
  }
  if (
    typeof value.visibility_snapshot !== 'string' ||
    value.visibility_snapshot.length > CAPTION_HYDRATION_LIMITS.visibilitySnapshotChars ||
    !VISIBILITY_SNAPSHOT.test(value.visibility_snapshot)
  ) {
    return null;
  }
  if (typeof value.phase !== 'string' || !PHASES.includes(value.phase as HydrationPhase)) {
    return null;
  }
  const phase = value.phase as HydrationPhase;
  const afterKeys = CURSOR_AFTER_KEYS[phase];
  const hasAfter = afterKeys.some((key) => Object.prototype.hasOwnProperty.call(value, key));
  const expectedKeys = hasAfter ? [...CURSOR_BASE_KEYS, ...afterKeys] : CURSOR_BASE_KEYS;
  if (!hasExactKeys(value, expectedKeys)) return null;

  let after: unknown[] | null = null;
  if (hasAfter) {
    if (phase === 'source_turns') {
      if (
        typeof value.after_stable_participant_id !== 'string' ||
        value.after_stable_participant_id.length === 0 ||
        codePointLength(value.after_stable_participant_id) > 256 ||
        !isSafeNonnegativeInteger(value.after_source_epoch) ||
        (value.after_source_epoch as number) < 1 ||
        !isSafeNonnegativeInteger(value.after_ordinal) ||
        typeof value.after_source_turn_id !== 'string' ||
        !CONTRACT_UUID.test(value.after_source_turn_id)
      ) {
        return null;
      }
      after = [
        value.after_stable_participant_id,
        value.after_source_epoch,
        value.after_ordinal,
        value.after_source_turn_id,
      ];
    } else if (phase === 'revisions') {
      if (
        typeof value.after_source_turn_id !== 'string' ||
        !CONTRACT_UUID.test(value.after_source_turn_id) ||
        !isSafeNonnegativeInteger(value.after_revision_index) ||
        typeof value.after_translation_revision_id !== 'string' ||
        !CONTRACT_UUID.test(value.after_translation_revision_id)
      ) {
        return null;
      }
      after = [
        value.after_source_turn_id,
        value.after_revision_index,
        value.after_translation_revision_id,
      ];
    } else {
      if (
        typeof value.after_source_turn_id !== 'string' ||
        !CONTRACT_UUID.test(value.after_source_turn_id)
      ) {
        return null;
      }
      after = [value.after_source_turn_id];
    }
  }

  return {
    raw: value,
    phase,
    visibilitySnapshot: value.visibility_snapshot,
    hasAfter,
    canonical: JSON.stringify([
      value.contract_version,
      value.call_id,
      value.target_language,
      value.visibility_snapshot,
      phase,
      after,
    ]),
    afterCanonical: after === null ? null : JSON.stringify(after),
  };
}

function lastItemAfterCanonical(phase: HydrationPhase, item: unknown): string | null {
  if (!isPlainRecord(item)) return null;
  if (phase === 'source_turns') {
    if (!isPlainRecord(item.source_turn)) return null;
    const turn = item.source_turn;
    return JSON.stringify([
      turn.stable_participant_id,
      turn.source_epoch,
      turn.ordinal,
      turn.source_turn_id,
    ]);
  }
  if (phase === 'revisions') {
    return JSON.stringify([item.source_turn_id, item.revision_index, item.translation_revision_id]);
  }
  return JSON.stringify([item.source_turn_id]);
}

/** The v3 revisions phase has no source body. Check every revision-side fact
 * here; selected bundles also pass the full source-aware coordinate validator. */
function validRevisionCoordinate(raw: unknown, revision: AttachedTranslationRevisionV1): boolean {
  if (raw === null) return true;
  if (!isPlainRecord(raw) || !hasExactKeys(raw, COORDINATE_KEYS) ||
      raw.contract_version !== 'live-translation.engine-coordinate.v1') return false;
  for (const key of [
    'call_id', 'translation_revision_id', 'source_turn_id', 'translation_session_record_id',
  ] as const) {
    const value = raw[key];
    if (typeof value !== 'string' || !DATABASE_UUID.test(value) || value === NIL_UUID) {
      return false;
    }
  }
  const group = raw.allocation_group_id;
  if (group !== null &&
      (typeof group !== 'string' || !DATABASE_UUID.test(group) || group === NIL_UUID)) {
    return false;
  }
  const start = raw.engine_range_start;
  const end = raw.engine_range_end;
  const sourceEpoch = raw.source_epoch;
  if (typeof raw.source_stable_participant_id !== 'string' ||
      raw.source_stable_participant_id.length === 0 ||
      typeof raw.source_track_sid !== 'string' || raw.source_track_sid.length === 0 ||
      !isSafeNonnegativeInteger(sourceEpoch) || sourceEpoch < 1 ||
      !isSafeNonnegativeInteger(raw.assignment_generation) ||
      !isSafeNonnegativeInteger(raw.route_generation) ||
      !isSafeNonnegativeInteger(start) || !isSafeNonnegativeInteger(end) ||
      end < start) return false;
  return raw.call_id === revision.call_id &&
    raw.translation_revision_id === revision.translation_revision_id &&
    raw.source_turn_id === revision.source_turn_id &&
    raw.target_language === revision.target_language &&
    raw.assignment_generation === revision.assignment_generation &&
    raw.route_generation === revision.route_generation &&
    raw.allocation_group_id === revision.allocation_group_id &&
    start === revision.engine_range_start && end === revision.engine_range_end &&
    revision.origin === 'engine' &&
    end - start === codePointLength(revision.text);
}

export function validateCaptionHydrationV3Item(phase: HydrationPhase, item: unknown, callUuid: string,
  targetLanguage: string): boolean {
  if (!isPlainRecord(item)) return false;
  if (phase === 'source_turns') {
    if (!hasExactKeys(item, SOURCE_ITEM_KEYS) || !validDbSourceTurn(item.source_turn)) {
      return false;
    }
    const source = item.source_turn as SourceTurnV1;
    return source.call_id === callUuid &&
      validPersistedTranscriptSyncId(item.transcript_sync_id);
  }
  if (phase === 'unavailable') {
    return hasExactKeys(item, UNAVAILABLE_ITEM_KEYS) &&
      typeof item.source_turn_id === 'string' && DATABASE_UUID.test(item.source_turn_id) &&
      item.target_language === targetLanguage &&
      typeof item.reason_code === 'string' && UNAVAILABLE_REASONS.has(item.reason_code);
  }
  const selected = phase === 'selections';
  if (!hasExactKeys(item, selected ? V3_SELECTED_ITEM_KEYS : V3_REVISION_ITEM_KEYS)) {
    return false;
  }
  const checked = validateTranslationRevisionRecord(item.revision);
  if (!checked.valid || checked.attachment === null) return false;
  const revision = checked.record as AttachedTranslationRevisionV1;
  if (revision.call_id !== callUuid || revision.target_language !== targetLanguage ||
      !validRevisionCoordinate(item.coordinate, revision)) return false;
  if (!selected) return true;
  if (!validDbSourceTurn(item.source_turn) ||
      !isPlainRecord(item.selection) ||
      !hasExactKeys(item.selection, V3_SELECTION_KEYS)) return false;
  const source = item.source_turn as SourceTurnV1;
  const selection = item.selection;
  if (source.call_id !== callUuid ||
      revision.source_turn_id !== source.source_turn_id ||
      selection.source_turn_id !== source.source_turn_id ||
      selection.target_language !== targetLanguage ||
      selection.selected_revision_id !== revision.translation_revision_id ||
      !isSafeNonnegativeInteger(selection.selection_generation) ||
      !isSafeNonnegativeInteger(selection.selected_at_ms) ||
      selection.selected_at_ms < MIN_SELECTION_TIME_MS ||
      typeof selection.selection_reason !== 'string' ||
      !V3_SELECTION_REASONS.has(selection.selection_reason) ||
      !validPersistedTranscriptSyncId(item.transcript_sync_id)) {
    return false;
  }
  return item.coordinate === null || validateEngineCoordinateV1(
    item.coordinate, source, revision, {
      source_turn_id: source.source_turn_id,
      target_language: revision.target_language,
      selected_revision_id: revision.translation_revision_id,
      selection_generation: selection.selection_generation as number,
    },
  ).valid;
}

function v3LastItemAfterCanonical(phase: HydrationPhase, item: unknown): string | null {
  if (!isPlainRecord(item)) return null;
  if (phase === 'revisions') return lastItemAfterCanonical(phase, item.revision);
  if (phase === 'selections') {
    return isPlainRecord(item.source_turn)
      ? JSON.stringify([item.source_turn.source_turn_id]) : null;
  }
  return lastItemAfterCanonical(phase, item);
}


export function parseCaptionHydrationV3Cursor(
  value: unknown, expected: { callUuid: string; targetLanguage: string },
): ParsedCursor | null {
  return readCursor(value, expected);
}

export type ValidatedCaptionHydrationV3Page =
  | { valid: true; page: CaptionHydrationV3Page; producerTrustValidForMs: number | null }
  | { valid: false };

/** Validate one page against the exact request cursor, without accumulating or
 * limiting source/revision text or the number of pages in a call. */
export function validateCaptionHydrationV3Page(
  raw: unknown, expected: {
    callUuid: string; targetLanguage: string; cursor: ParsedCursor | null;
  },
): ValidatedCaptionHydrationV3Page {
  const invalid: ValidatedCaptionHydrationV3Page = { valid: false };
  if (!isPlainRecord(raw) || !hasExactKeys(raw, V3_PAGE_KEYS) ||
      raw.contract_version !== V3_PAGE_CONTRACT ||
      raw.call_id !== expected.callUuid ||
      raw.target_language !== expected.targetLanguage ||
      typeof raw.visibility_snapshot !== 'string' ||
      raw.visibility_snapshot.length > CAPTION_HYDRATION_LIMITS.visibilitySnapshotChars ||
      !VISIBILITY_SNAPSHOT.test(raw.visibility_snapshot) ||
      typeof raw.phase !== 'string' || !PHASES.includes(raw.phase as HydrationPhase) ||
      raw.page_size !== CAPTION_HYDRATION_LIMITS.rpcPageSize ||
      !Array.isArray(raw.items) || raw.items.length > CAPTION_HYDRATION_LIMITS.rpcPageSize ||
      !raw.items.every((item) => validateCaptionHydrationV3Item(raw.phase as HydrationPhase, item,
        expected.callUuid, expected.targetLanguage)) ||
      typeof raw.has_more !== 'boolean' || typeof raw.complete !== 'boolean' ||
      !isSafeNonnegativeInteger(raw.authorized_worker_identities_valid_for_ms) ||
      raw.authorized_worker_identities_valid_for_ms >
        CAPTION_HYDRATION_LIMITS.producerTrustValidityMs ||
      !Array.isArray(raw.authorized_worker_identities) ||
      raw.authorized_worker_identities.length >
        CAPTION_HYDRATION_LIMITS.authorizedWorkerIdentities ||
      (raw.outcome !== 'page' && raw.outcome !== 'snapshot_changed')) return invalid;
  const phase = raw.phase as HydrationPhase;
  if (expected.cursor === null) {
    if (raw.cursor !== null || phase !== 'source_turns') return invalid;
  } else {
    const echoed = parseCaptionHydrationV3Cursor(raw.cursor, expected);
    if (!echoed || echoed.canonical !== expected.cursor.canonical ||
        raw.visibility_snapshot !== expected.cursor.visibilitySnapshot ||
        phase !== expected.cursor.phase) return invalid;
  }
  if (raw.outcome === 'snapshot_changed' || raw.complete) {
    if (phase !== 'unavailable' || raw.has_more || raw.next_cursor !== null) return invalid;
    if (raw.outcome === 'snapshot_changed') {
      if (raw.complete || raw.authorized_worker_identities_valid_for_ms !== 0 ||
          raw.authorized_worker_identities.length !== 0) return invalid;
      return { valid: true, page: raw as unknown as CaptionHydrationV3Page,
        producerTrustValidForMs: null };
    }
    if (raw.authorized_worker_identities_valid_for_ms <
        CAPTION_HYDRATION_LIMITS.producerTrustServerMinimumValidityMs) return invalid;
    const identities = new Set<string>();
    for (const identity of raw.authorized_worker_identities) {
      if (typeof identity !== 'string' || codePointLength(identity) === 0 ||
          codePointLength(identity) > CAPTION_HYDRATION_LIMITS.workerIdentityChars ||
          identities.has(identity)) return invalid;
      identities.add(identity);
    }
    return { valid: true, page: raw as unknown as CaptionHydrationV3Page,
      producerTrustValidForMs: raw.authorized_worker_identities_valid_for_ms };
  }
  if (!raw.has_more || raw.next_cursor === null ||
      raw.authorized_worker_identities_valid_for_ms !== 0 ||
      raw.authorized_worker_identities.length !== 0) return invalid;
  const next = parseCaptionHydrationV3Cursor(raw.next_cursor, expected);
  if (!next || next.visibilitySnapshot !== raw.visibility_snapshot ||
      next.canonical === expected.cursor?.canonical) return invalid;
  const phaseDelta = phaseIndex(next.phase) - phaseIndex(phase);
  if (phaseDelta < 0 || phaseDelta > 1) return invalid;
  if (phaseDelta === 0) {
    if (raw.items.length !== CAPTION_HYDRATION_LIMITS.rpcPageSize || !next.hasAfter ||
        next.afterCanonical !== v3LastItemAfterCanonical(phase, raw.items.at(-1))) return invalid;
  } else if (next.hasAfter) return invalid;
  return { valid: true, page: raw as unknown as CaptionHydrationV3Page,
    producerTrustValidForMs: null };
}
